import React from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Smartphone,
  Utensils,
  Clock,
  Download,
  ShieldCheck,
  CreditCard,
  Lock,
  Globe,
  Layout,
  Layers,
  Zap,
  Mail,
  Battery,
  Wifi,
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ProductPageLayout from '../components/ProductPageLayout';

// Hero — N62 dual device (flagship hardware as the hero)
const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/9uqvgcqo_N62%20AS%20NEW%20HERO%20IMAGE.png';
// Under "Two Approaches, One Goal" intro — SoftPOS phone shot
const N62_INTRO_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/jfzy556m_SOFTPOS%20IMAGE.png';
// Between SoftPOS card and MPOS card — clearer N62 (front + back)
const N62_SHOWCASE_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/adijmu2m_N62.png';

/* ─────────────────────────────────────── HERO (mirrors POS exactly) ─────────────────────────────────────── */
const SoftPosHero = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromHome = ['/', 'footer'].includes(location.state?.from);
  const fromProductNav = location.state?.from === 'product-pagination';
  const productNavReturnTo = location.state?.returnTo;
  const fromTopNav = location.state?.from === 'top-nav';
  return (
    <section className="relative bg-[#f7f6f3]" data-testid="softpos-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-10">
        <button
          onClick={() => {
            if (fromTopNav) navigate('/');
            else if (fromProductNav && productNavReturnTo) navigate(productNavReturnTo);
            else if (fromHome) navigate(-1);
            else navigate('/products');
          }}
          className="relative z-20 self-start flex items-center gap-2 text-gray-600 hover:text-[#1F4E79] font-medium mb-8 transition-colors duration-200"
          data-testid="product-back-button"
        >
          <ArrowLeft size={18} />
          {fromTopNav ? 'Back to Home' : (fromHome || fromProductNav) ? 'Back' : 'Back to Products'}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left — title + subtitle + CTA (smaller column so the hero image dominates, like POS) */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ef5b25]/10 border border-[#ef5b25]/30 text-[#c44516] text-[11px] font-bold uppercase tracking-[0.15em] mb-5"
              data-testid="softpos-eyebrow"
            >
              On-the-Go Payments
            </div>
            <h1
              className="text-5xl sm:text-6xl lg:text-[88px] font-bold text-gray-900 leading-[1.05] tracking-tight mb-6"
              data-testid="product-title"
            >
              SoftPOS<br />& MPOS
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-xl mb-9">
              Smartphone payments or dedicated mobile hardware — flexibility for businesses on the move.
            </p>
            <Link
              to="/get-started-softpos"
    onClick={() => window.__resetSourceScroll?.()}
              className="group inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#1F4E79] text-white font-bold rounded-lg hover:bg-[#163a5f] hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg"
              data-testid="softpos-cta-button"
            >
              Get Started Here</Link>
          </div>

          {/* Right — Hero image (DOMINANT, like the N96 picture on POS) */}
          <div className="lg:col-span-7 order-1 lg:order-2 flex items-center justify-center overflow-hidden lg:overflow-visible">
            <img
              src={HERO_IMAGE}
              alt="eCOMM N62 — dedicated mobile payment terminal, dual angle view"
              className="object-contain scale-[1.3] sm:scale-[1.45]"
              style={{ maxWidth: '1100px', width: '100%', height: 'auto' }}
              loading="eager"
              fetchpriority="high"
              decoding="async"
              data-testid="hero-softpos-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────── PRODUCT MOMENT (mirrors EPOS Family) ─────────────────────────────────────── */
const TwoApproachesMoment = () => (
  <section className="relative pt-16 sm:pt-20 lg:pt-24" data-testid="softpos-two-approaches">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.2em] mb-4">
        Two Approaches, One Goal
      </p>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight tracking-tight mb-5">
        Smart Software<br />Reliable Hardware
      </h2>
      <p className="text-lg text-gray-900 font-bold leading-relaxed max-w-3xl mx-auto mb-4">
        Take payments wherever your business operates. With eCOMM's mobile solutions, you can use your own device or a dedicated terminal to accept fast, secure payments on the move.
      </p>
    </div>

    {/* SoftPOS phone image — naked on the page, soft radial mask fades the dark edges into the body */}
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 -mt-4 sm:-mt-6">
      <img
        src={N62_INTRO_IMAGE}
        alt="eCOMM SoftPOS — Tap and Pay on any compatible Android device"
        className="w-full h-auto mx-auto block"
        style={{
          maskImage:
            'radial-gradient(ellipse 80% 75% at 50% 50%, #000 50%, rgba(0,0,0,0.85) 70%, rgba(0,0,0,0.35) 88%, rgba(0,0,0,0) 100%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 80% 75% at 50% 50%, #000 50%, rgba(0,0,0,0.85) 70%, rgba(0,0,0,0.35) 88%, rgba(0,0,0,0) 100%)',
        }}
        loading="eager"
        decoding="async"
        data-testid="softpos-n62-intro-image"
      />
    </div>
  </section>
);

/* ─────────────────────────────────────── SHARED UI ─────────────────────────────────────── */
const SectionHeading = ({ children, testId }) => (
  <div className="mb-6" data-testid={testId}>
    <h2 className="text-3xl font-bold text-gray-900 mb-2">{children}</h2>
    <div className="h-1 w-20 bg-[#1F4E79] rounded-full" />
  </div>
);

const PerfectFor = ({ tone = 'amber', children, testId }) => {
  const tones = {
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    sky: 'border-sky-200 bg-sky-50 text-sky-900',
  };
  return (
    <div className={`mt-6 rounded-md border p-4 text-sm ${tones[tone]}`} data-testid={testId}>
      <strong>Perfect For:</strong> {children}
    </div>
  );
};

const FeatureChip = ({ icon: Icon, children, testId }) => (
  <div
    className="flex items-start gap-3 rounded-xl bg-white border border-gray-200 hover:border-[#1F4E79]/40 hover:shadow-sm p-4 transition-all duration-200"
    data-testid={testId}
  >
    {Icon && (
      <div className="flex-shrink-0 mt-0.5 text-[#1F4E79]">
        <Icon size={20} strokeWidth={1.75} />
      </div>
    )}
    <span className="text-[14px] text-gray-800 font-medium leading-snug">{children}</span>
  </div>
);

/* ─────────────────────────────────────── PAGE ─────────────────────────────────────── */
const ProductsSoftPosMpos = () => (
  <ProductPageLayout
    currentId="softpos-and-mpos"
    customHero={
      <>
        <SoftPosHero />
        <TwoApproachesMoment />
      </>
    }
    bodyBgClassName="bg-[#f7f6f3]"
    mainTestId="products-softpos-page"
  >
    {/* Our Mobile Solutions Range heading */}
    <SectionHeading testId="softpos-range-heading">Our Mobile Solutions Range</SectionHeading>

    {/* SoftPOS — Tap and Pay (gradient header card matching EPOS) */}
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-10" data-testid="softpos-section">
      <div className="bg-gradient-to-br from-[#1F4E79] to-[#0E88A3] p-6 sm:p-10">
        <p className="text-[11px] font-bold text-white/90 uppercase tracking-[0.2em] mb-2">Tap and Pay</p>
        <h3 className="text-3xl sm:text-4xl font-bold text-white leading-tight">SoftPOS</h3>
        <p className="text-white/95 mt-2 text-base sm:text-lg max-w-2xl">
          Turn your Android device into a fully functional payment terminal with no additional hardware required.
        </p>
      </div>
      <div className="p-6 sm:p-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FeatureChip icon={Download}    testId="softpos-f1">Download instantly from the Google Play Store</FeatureChip>
          <FeatureChip icon={Smartphone}  testId="softpos-f2">Works on Android devices with no additional hardware</FeatureChip>
          <FeatureChip icon={ShieldCheck} testId="softpos-f3">Fully MPOC compliant for secure payment acceptance</FeatureChip>
          <FeatureChip icon={CreditCard}  testId="softpos-f4">Contactless, Chip and PIN payments</FeatureChip>
          <FeatureChip icon={Lock}        testId="softpos-f5">Supports Apple Pay, Google Pay and all major cards</FeatureChip>
          <FeatureChip icon={Globe}       testId="softpos-f6">Accept payments anywhere with complete mobility</FeatureChip>
        </div>
        <PerfectFor tone="sky" testId="softpos-perfect">
          Micro-Merchants, Pop-Up Retailers, Delivery Drivers, Service Providers and Any Business Needing Instant Payment Capabilities.
        </PerfectFor>
      </div>
    </div>

    {/* Second N62 image — showcase moment between SoftPOS and MPOS cards */}
    <div className="mb-10 -mx-2 sm:-mx-4" data-testid="softpos-n62-showcase">
      <img
        src={N62_SHOWCASE_IMAGE}
        alt="eCOMM N62 dedicated mobile terminal — front and rear views"
        className="w-full h-auto mx-auto block"
        style={{
          maskImage:
            'radial-gradient(ellipse 82% 78% at 50% 50%, #000 50%, rgba(0,0,0,0.85) 70%, rgba(0,0,0,0.35) 88%, rgba(0,0,0,0) 100%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 82% 78% at 50% 50%, #000 50%, rgba(0,0,0,0.85) 70%, rgba(0,0,0,0.35) 88%, rgba(0,0,0,0) 100%)',
          maxWidth: '1250px',
        }}
        loading="lazy"
        decoding="async"
        data-testid="softpos-n62-showcase-image"
      />
    </div>

    {/* MPOS — N62 (gradient header card) */}
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-10" data-testid="mpos-n62-section">
      <div className="bg-gradient-to-br from-[#0a1929] to-[#163a5f] p-6 sm:p-10">
        <p className="text-[11px] font-bold text-[#ffcba6] uppercase tracking-[0.2em] mb-2">Dedicated Mobile Hardware</p>
        <h3 className="text-3xl sm:text-4xl font-bold text-white leading-tight">MPOS — N62</h3>
        <p className="text-white/85 mt-2 text-base sm:text-lg max-w-2xl">
          A compact, dedicated device for professional grade mobile payment processing.
        </p>
      </div>
      <div className="p-6 sm:p-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FeatureChip icon={Layout}     testId="n62-f1">Compact 4-inch design, lightweight and portable</FeatureChip>
          <FeatureChip icon={Layers}     testId="n62-f2">Custom eCOMM software tailored to your business</FeatureChip>
          <FeatureChip icon={Zap}        testId="n62-f3">Reliable, fast and secure payment processing</FeatureChip>
          <FeatureChip icon={Mail}       testId="n62-f4">Automatic email receipts for every transaction</FeatureChip>
          <FeatureChip icon={Battery}    testId="n62-f5">Long-lasting battery for all day performance</FeatureChip>
          <FeatureChip icon={Wifi}       testId="n62-f6">WiFi and 4G connectivity for payments on the move</FeatureChip>
        </div>
        <PerfectFor tone="amber" testId="n62-perfect">
          Field Sales Teams, Trade Professionals, Market Vendors and Businesses Requiring Dedicated Mobile Payment Hardware.
        </PerfectFor>
      </div>
    </div>

    {/* Final CTA strip — same dark gradient as EPOS / POS */}
    <div
      className="rounded-xl bg-gradient-to-br from-[#0a1929] to-[#163a5f] p-8 sm:p-12 text-center shadow-lg mb-2"
      data-testid="softpos-final-cta"
    >
      <h3 className="text-3xl sm:text-4xl font-bold text-white leading-tight tracking-tight mb-4">
        Ready To Take Payments Anywhere?
      </h3>
      <p className="text-white/90 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
        Speak to our team and we'll match you with the right mobile payment setup for your business.
      </p>
      <Link
        to="/get-started-softpos"
    onClick={() => window.__resetSourceScroll?.()}
        className="group inline-flex items-center gap-2.5 px-7 py-3.5 bg-white text-[#1F4E79] font-bold rounded-lg hover:bg-white/90 hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg"
        data-testid="softpos-final-cta-button"
      >
        Get Started Here</Link>
    </div>
  </ProductPageLayout>
);

export default ProductsSoftPosMpos;
